const SETTINGS_CACHE_TTL = 24 * 60 * 60 * 1000
const SETTINGS_REQUEST_INTERVAL = 60 * 60 * 1000
const SETTINGS_REQUEST_LOCK_PREFIX = 'ticket-place:fetch-settings'
const SETTINGS_REQUEST_STATE_KEY_PREFIX = 'fetch-settings-state'

const inFlightRequests = new Map()

const getStateKey = source => `${SETTINGS_REQUEST_STATE_KEY_PREFIX}:${source}`

const readState = source => {
  try {
    const value = localStorage.getItem(getStateKey(source))
    if (!value) return {}

    const state = JSON.parse(value)
    if (!Number.isFinite(state.nextRequestAt || 0) || !Number.isFinite(state.cachedAt || 0)) {
      throw new Error('Invalid settings request state')
    }

    return state
  } catch (error) {
    try {
      localStorage.removeItem(getStateKey(source))
    } catch {
      // localStorage remains inaccessible; rethrow the original error below.
    }
    throw error
  }
}

const writeState = (source, state) => localStorage.setItem(getStateKey(source), JSON.stringify(state))

const isFresh = state => state.settings && Date.now() - state.cachedAt < SETTINGS_CACHE_TTL

const requestSharedSettings = async (source, request) => {
  if (inFlightRequests.has(source)) return inFlightRequests.get(source)

  const locks = typeof navigator === 'undefined' ? null : navigator.locks
  if (!locks || typeof locks.request !== 'function') {
    const state = readState(source)
    if (state.settings) return state.settings

    throw new Error('Web Locks API is unavailable and no cached settings snapshot exists')
  }

  const lockName = `${SETTINGS_REQUEST_LOCK_PREFIX}:${source}`
  const inFlightRequest = locks.request(lockName, async () => {
    const currentState = readState(source)
    if (isFresh(currentState)) return currentState.settings

    const now = Date.now()
    if (currentState.nextRequestAt > now) {
      if (currentState.settings) return currentState.settings

      throw new Error('Settings request is throttled without a cached snapshot')
    }

    const state = { ...currentState, nextRequestAt: now + SETTINGS_REQUEST_INTERVAL }
    writeState(source, state)

    try {
      const settings = await request()
      writeState(source, { settings, cachedAt: Date.now(), nextRequestAt: state.nextRequestAt })
      return settings
    } catch (error) {
      writeState(source, currentState)
      throw error
    }
  }).finally(() => {
    inFlightRequests.delete(source)
  })

  inFlightRequests.set(source, inFlightRequest)
  return inFlightRequest
}

export default requestSharedSettings
